import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Crop,
  Sparkle,
  MagicWand,
  X,
  Check,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";

// Crop, HD enhancement and background removal for one photo. Edits stack up;
// onDone hands back the finished File (file.enhanced = true once the HD pass
// ran, so the upload can skip the automatic one) for the caller to upload.
// keep filenames from stacking up -crop-nobg-crop-nobg on repeat edits
const baseName = (name) =>
  name.replace(/\.\w+$/, "").replace(/(-crop|-nobg|-hd)+$/, "");

const blobErrorDetail = async (err) => {
  try {
    return formatApiErrorDetail(
      JSON.parse(await err.response?.data?.text())?.detail,
    );
  } catch {
    return "";
  }
};

const isHeic = (f) =>
  /image\/hei[cf]/i.test(f?.type || "") || /\.hei[cf]$/i.test(f?.name || "");

export const PhotoEditor = ({ file, onDone, onClose }) => {
  const imgRef = useRef(null);
  const boxRef = useRef(null);
  const drag = useRef(null);
  const [working, setWorking] = useState(file);
  const [src, setSrc] = useState(() => URL.createObjectURL(file));
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(() => (isHeic(file) ? "convert" : ""));
  const [edits, setEdits] = useState([]);
  const [hd, setHd] = useState(false);
  const [size, setSize] = useState(null);
  const [base, setBase] = useState(file);
  const [prompt, setPrompt] = useState("");

  // iPhone HEIC can't be shown by most browsers (or cropped on a canvas) — the server
  // turns it into a JPEG first, and that JPEG becomes the "original" for Reset.
  // Oversized shots are shrunk in the browser so every later step stays under 12 MB.
  useEffect(() => {
    let alive = true;
    const settle = (jpg, note) => {
      if (!alive) return;
      setBase(jpg);
      setWorking(jpg);
      setEdits(note ? [note] : []);
    };
    if (isHeic(file)) {
      const fd = new FormData();
      fd.append("file", file);
      api
        .post("/photos/to-jpeg", fd, { responseType: "blob" })
        .then(async ({ data }) => {
          const jpg = await shrinkForUpload(
            new File([data], baseName(file.name) + ".jpg", { type: "image/jpeg" }),
          );
          settle(jpg, "Converted from iPhone HEIC");
        })
        .catch(() => {
          if (alive) toast.error("Could not read that photo — try taking it again");
        })
        .finally(() => alive && setBusy(""));
    } else {
      shrinkForUpload(file).then((out) => {
        if (out !== file) settle(out, "Resized for upload");
      });
    }
    return () => {
      alive = false;
    };
  }, [file]);

  const onImgLoad = () => {
    const img = imgRef.current;
    if (img) setSize({ w: img.naturalWidth, h: img.naturalHeight });
  };

  useEffect(() => {
    const url = URL.createObjectURL(working);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [working]);

  // Fractions of the picture itself, so the crop lands on the right pixels.
  const rel = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  const down = (e) => {
    e.preventDefault();
    const start = rel(e);
    drag.current = start;
    setSel(null);
    const onMove = (ev) => {
      const p = rel(ev);
      setSel({
        x: Math.min(start.x, p.x),
        y: Math.min(start.y, p.y),
        w: Math.abs(p.x - start.x),
        h: Math.abs(p.y - start.y),
      });
    };
    const onUp = () => {
      drag.current = null;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    document.addEventListener("mouseup", onUp);
  };

  // The frame hugs the picture exactly, so the box maps straight onto its pixels.
  const applyCrop = async () => {
    if (!sel || sel.w < 0.02 || sel.h < 0.02) {
      toast.error("Drag a box on the photo first");
      return;
    }
    const img = imgRef.current;
    const x = Math.round(img.naturalWidth * sel.x);
    const y = Math.round(img.naturalHeight * sel.y);
    const w = Math.round(img.naturalWidth * sel.w);
    const h = Math.round(img.naturalHeight * sel.h);
    if (w < 16 || h < 16) {
      toast.error("That box is too small — drag a bigger one");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.92));
    setWorking(
      new File([blob], baseName(working.name) + "-crop.jpg", {
        type: "image/jpeg",
      }),
    );
    setSel(null);
    setEdits((e) => [...e, `Cropped to ${w}×${h}`]);
    toast.success("Cropped");
  };

  const enhance = async (mode) => {
    setBusy(mode);
    try {
      const fd = new FormData();
      fd.append("file", working);
      fd.append("mode", mode);
      const { data } = await api.post("/photos/enhance", fd, {
        responseType: "blob",
      });
      setWorking(
        new File([data], baseName(working.name) + "-hd.jpg", {
          type: "image/jpeg",
        }),
      );
      setHd(true);
      setSel(null);
      setEdits((e) => [
        ...e,
        mode === "clean" ? "HD + studio clean-up" : "HD enhanced",
      ]);
      toast.success("HD version ready — check the veining still matches");
    } catch (err) {
      toast.error((await blobErrorDetail(err)) || "Enhancement failed. Try again.");
    }
    setBusy("");
  };

  const aiEdit = async () => {
    const p = prompt.trim();
    if (!p) {
      toast.error("Describe the edit you want first");
      return;
    }
    setBusy("ai");
    try {
      const fd = new FormData();
      fd.append("file", working);
      fd.append("prompt", p);
      const { data } = await api.post("/photos/ai-edit", fd, {
        responseType: "blob",
      });
      const kind = data.type || "image/jpeg";
      const ext = kind === "image/png" ? "png" : "jpg";
      setWorking(
        new File([data], baseName(working.name) + "-ai." + ext, { type: kind }),
      );
      setSel(null);
      setEdits((e) => [...e, `AI edit: ${p.length > 42 ? p.slice(0, 42) + "…" : p}`]);
      setPrompt("");
      toast.success("AI edit ready — check it looks right");
    } catch (err) {
      toast.error((await blobErrorDetail(err)) || "AI edit failed. Try again.");
    }
    setBusy("");
  };

  const removeBg = async (style) => {
    setBusy(style);
    try {
      const fd = new FormData();
      fd.append("file", working);
      fd.append("style", style);
      const { data } = await api.post("/photos/remove-background", fd, {
        responseType: "blob",
      });
      const kind = data.type || "image/jpeg";
      const ext = kind === "image/png" ? "png" : "jpg";
      setWorking(
        new File([data], baseName(working.name) + "-nobg." + ext, {
          type: kind,
        }),
      );
      setSel(null);
      setEdits((e) => [
        ...e,
        style === "white" ? "Background removed" : "Studio backdrop",
      ]);
      toast.success("Background done — check it looks right");
    } catch (err) {
      toast.error(
        (await blobErrorDetail(err)) || "Background removal failed. Try again.",
      );
    }
    setBusy("");
  };

  const reset = () => {
    setWorking(base);
    setSel(null);
    setEdits(
      base === file ? [] : isHeic(file) ? ["Converted from iPhone HEIC"] : ["Resized for upload"],
    );
    setHd(false);
  };

  const finish = () => {
    const out = working;
    out.enhanced = hd;
    onDone(out);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      data-testid="photo-editor"
    >
      <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-900 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Tidy up the photo
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Drag a box on the picture to crop, make it HD, or drop the yard
              out of the background.
            </p>
            {size && (
              <p
                className="font-mono-data mt-1 text-[10px] text-zinc-500"
                data-testid="photo-editor-size"
              >
                {size.w} × {size.h}
                {hd ? " · HD" : ""}
              </p>
            )}
          </div>
          <button
            type="button"
            data-testid="photo-editor-close"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-zinc-400 hover:text-white"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="mt-3 flex justify-center">
          <div
            ref={boxRef}
            onPointerDown={down}
            style={{ touchAction: "none" }}
            className="relative inline-block max-w-full select-none overflow-hidden rounded-md border border-zinc-800 bg-[repeating-conic-gradient(#1f1f22_0_25%,#27272a_0_50%)] bg-[length:18px_18px] leading-none"
          >
            <img
              ref={imgRef}
              src={src}
              alt=""
              onLoad={onImgLoad}
              className="block max-h-[52vh] max-w-full object-contain"
            />
            {sel && (
              <div
                data-testid="photo-crop-selection"
                className="pointer-events-none absolute border-2 border-yellow-500 bg-yellow-500/15"
                style={{
                  left: `${sel.x * 100}%`,
                  top: `${sel.y * 100}%`,
                  width: `${sel.w * 100}%`,
                  height: `${sel.h * 100}%`,
                }}
              >
                <span className="absolute -top-6 left-0 whitespace-nowrap rounded bg-yellow-500 px-1.5 py-0.5 text-[10px] font-bold text-black">
                  {Math.round((imgRef.current?.naturalWidth || 0) * sel.w)} ×{" "}
                  {Math.round((imgRef.current?.naturalHeight || 0) * sel.h)}
                </span>
              </div>
            )}
            {busy && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/70 text-center text-xs uppercase tracking-widest text-zinc-200"
                data-testid="photo-editor-busy"
              >
                <span>{busy === "convert" ? "Reading iPhone photo…" : "Working on it…"}</span>
                {(busy === "faithful" || busy === "clean") && (
                  <span className="text-[10px] normal-case tracking-normal text-zinc-400">
                    HD takes 15–45 seconds
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {edits.length > 0 && (
          <div
            className="mt-2 flex flex-wrap gap-1.5"
            data-testid="photo-editor-edits"
          >
            {edits.map((e, i) => (
              <span
                key={i}
                className="rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[10px] uppercase tracking-widest text-zinc-400"
              >
                {e}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row" data-testid="photo-ai-bar">
          <input
            data-testid="photo-ai-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                aiEdit();
              }
            }}
            disabled={!!busy}
            placeholder="AI edit — e.g. remove the ladder, brighten the room, add a clear blue sky"
            className="h-11 flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-fuchsia-500 focus:outline-none"
          />
          <Button
            type="button"
            data-testid="photo-ai-edit"
            disabled={!!busy || !prompt.trim()}
            onClick={aiEdit}
            className="h-11 shrink-0 bg-fuchsia-500 text-xs font-bold uppercase tracking-widest text-black hover:bg-fuchsia-400"
          >
            <MagicWand size={15} weight="bold" className="mr-2" />
            {busy === "ai" ? "Editing…" : "AI edit"}
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            data-testid="photo-crop-apply"
            disabled={!!busy}
            onClick={applyCrop}
            className="h-11 bg-zinc-100 text-xs font-bold uppercase tracking-widest text-zinc-900 hover:bg-white"
          >
            <Crop size={15} weight="bold" className="mr-2" /> Crop
          </Button>
          <Button
            type="button"
            data-testid="photo-enhance-hd"
            disabled={!!busy}
            onClick={() => enhance("faithful")}
            className="h-11 bg-emerald-500 text-xs font-bold uppercase tracking-widest text-black hover:bg-emerald-400"
          >
            <MagicWand size={15} weight="bold" className="mr-2" />
            {busy === "faithful" ? "Enhancing…" : "Enhance HD"}
          </Button>
          <Button
            type="button"
            data-testid="photo-enhance-clean"
            variant="outline"
            disabled={!!busy}
            onClick={() => enhance("clean")}
            className="h-11 border-emerald-700/60 bg-transparent text-xs font-semibold uppercase tracking-widest text-emerald-200 hover:bg-emerald-950"
          >
            <MagicWand size={15} weight="bold" className="mr-2" />
            {busy === "clean" ? "Enhancing…" : "HD + clean backdrop"}
          </Button>
          <Button
            type="button"
            data-testid="photo-remove-bg"
            variant="outline"
            disabled={!!busy}
            onClick={() => removeBg("white")}
            className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
          >
            <Sparkle size={15} weight="bold" className="mr-2" />
            {busy === "white" ? "Removing…" : "Remove background"}
          </Button>
          <Button
            type="button"
            data-testid="photo-studio-bg"
            variant="outline"
            disabled={!!busy}
            onClick={() => removeBg("studio")}
            className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
          >
            <Sparkle size={15} weight="bold" className="mr-2" />
            {busy === "studio" ? "Working…" : "Studio backdrop"}
          </Button>
          {edits.length > 0 && (
            <Button
              type="button"
              data-testid="photo-editor-reset"
              variant="outline"
              disabled={!!busy}
              onClick={reset}
              className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
            >
              <ArrowCounterClockwise size={15} weight="bold" className="mr-2" />
              Start again
            </Button>
          )}
          <Button
            type="button"
            data-testid="photo-editor-done"
            disabled={!!busy}
            onClick={finish}
            className="ml-auto h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <Check size={15} weight="bold" className="mr-2" /> Use this photo
          </Button>
        </div>
      </div>
    </div>
  );
};
